import withAuth from "components/decorator/WithAuth"
import IeltsEliteProgressiveForm from "components/Ielts/IeltsOnboardingForms/IeltsEliteProgressiveForm"
import { NextPage } from "next"

import { Button } from "@leap/ui/Buttons"
import {
  fetchGetIeltsStage,
  fetchGetStudentEligibility,
  IeltsStageType,
  updatePostGetStageAction,
  updatePostGetStudentEligibility,
} from "components/Ielts/IeltsEliteDemo/redux/IeltsEliteSlice"
import {
  ieltsDemoMasterclassJson,
  ieltsEliteJson,
} from "components/Ielts/IeltsOnboardingForms/IeltsEliteProgressiveForm/ieltsEliteFormJson"
import { referAndEarnActions } from "components/Profile/ReferAndEarn/redux/ReferAndEarnActions"
import { State } from "Interfaces/storeSchema"
import { useRouter } from "next/router"
import { navRoutes } from "page_routes"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { redirectOutFromIframe } from "utils/common_utils"
import IeltsEliteProgressiveFormV2 from "components/Ielts/IeltsOnboardingForms/IeltsEliteProgressiveForm/V2"
import { ieltsEliteJsonV2 } from "components/Ielts/IeltsOnboardingForms/IeltsEliteProgressiveForm/V2/ieltsEliteFormJsonV2"
import useDemoAlias from "hooks/ielts/useDemoAlias"
import LineBreak from "components/common/LineBreak"
import { trackEventAsync, trackIeltsEvent, trackPageV2 } from "scripts/segment"
import { ieltsPageName } from "utils/events-utils"
import { getFormId } from "components/Ielts/IeltsOnboardingForms/utils"
import Head from "next/head"

const checkFormVersionIsTwo = (form_version: string) => {
  return form_version === "2"
}
type Props = {}
const UserDetailsFormPage: NextPage<Props> = () => {
  const { auth } = useSelector((state: State) => state.auth)
  const dispatch = useDispatch()
  const router = useRouter()
  const [userState, setUserState] = useState<IeltsStageType | null>(null)
  const [showRedirectCta, setShowRedirectCta] = useState(false)
  const [prefilledData, setPrefilledData] = useState<Record<string, string>>({})
  const [demoBookFormJson, setDemoBookFormJson] = useState<any>(null)
  const { userInfo } = useSelector((state: State) => state.referAndEarn)
  const { form_version, isDisqualifiedUser } = router.query as {
    form_version: string
    isDisqualifiedUser: string
  }
  const isDisqualifiedUserFlow = isDisqualifiedUser === "true"
  const { isMasterclassAlias } = useDemoAlias()

  useEffect(() => {
    if (checkFormVersionIsTwo(form_version)) {
      trackIeltsEvent("Optimise selection", {
        optimizeSelection: "Base",
      })
    }
  }, [])

  useEffect(() => {
    if (auth?.isValid) {
      dispatch(referAndEarnActions.fetchUserInfo())
      if (!isDisqualifiedUserFlow) {
        dispatch(
          updatePostGetStageAction((studentStage) => {
            setUserState(studentStage)
            if (
              studentStage === IeltsStageType.IELTS_DEMO_BOOKED ||
              studentStage === IeltsStageType.IELTS_DEMO_ATTENDED
            ) {
              dispatch(
                updatePostGetStudentEligibility((eligibilityData) => {
                  if (
                    eligibilityData?.slotType &&
                    eligibilityData?.slotType >= 1 &&
                    eligibilityData?.slotType <= 3
                  ) {
                    trackPageV2("Go To Dashboard", {
                      formId: getFormId(
                        !!isDisqualifiedUser,
                        isMasterclassAlias,
                      ),
                      demoStage: studentStage,
                    })
                    setShowRedirectCta(true)
                  } else {
                    router.push(navRoutes.IELTS_DEMO_QL_PAGE)
                  }
                }),
              )
              dispatch(fetchGetStudentEligibility())
            } else if (
              studentStage === IeltsStageType.LS_USER_ACQUIRED &&
              !isDisqualifiedUserFlow
            ) {
              router.push(navRoutes.IELTS_DEMO_QL_PAGE)
            }
          }),
        )
        dispatch(fetchGetIeltsStage())
      }
    }
  }, [auth, isDisqualifiedUserFlow])

  useEffect(() => {
    if (!auth?.isValid || userInfo.phone) {
      getUserForm()
    }
  }, [userInfo, auth])

  const getDemoFlowJson = () =>
    isMasterclassAlias ? ieltsDemoMasterclassJson : ieltsEliteJson

  const getUserForm = async () => {
    const prefilledData: Record<string, string> = {}
    if (auth?.isValid) {
      if (userInfo?.phone) prefilledData["phone"] = userInfo?.phone
      if (userInfo?.email) prefilledData["emailId"] = userInfo?.email
      if (userInfo?.name) prefilledData["name"] = userInfo?.name
      if (
        prefilledData?.phone &&
        prefilledData?.emailId &&
        prefilledData?.name
      ) {
        const filterFields = ["emailId", "name", "phone"]
        if (checkFormVersionIsTwo(form_version)) {
          setDemoBookFormJson({
            ...ieltsEliteJsonV2,
            no_of_steps: 3,
            page_distribution: [1, 1, 1],
            fields: ieltsEliteJsonV2.fields.filter(
              (field: any) => !filterFields.includes(field.fieldValue),
            ),
          })
        } else {
          setDemoBookFormJson({
            ...getDemoFlowJson(),
            page_distribution: [4],
            is_auto_progressive: false,
            fields: getDemoFlowJson().fields.filter(
              (field: any) => !filterFields.includes(field.fieldValue),
            ),
          })
        }

        return setPrefilledData(prefilledData)
      }
    }
    if (checkFormVersionIsTwo(form_version)) {
      setDemoBookFormJson(ieltsEliteJsonV2)
    } else {
      setDemoBookFormJson(getDemoFlowJson())
    }
    return setPrefilledData(prefilledData)
  }

  const trackGoBackDashboardEvent = async () => {
    try {
      await trackEventAsync("Click", {
        pageName: isMasterclassAlias
          ? ieltsPageName.DEMO_MC_FORM_1
          : ieltsPageName.IELTS_ELITE_INPAGE_FORM_1,
        contentName: "Go To Dashboard",
        contentFormat: "Button",
        widgetFormat: "Button",
        formId: getFormId(!!isDisqualifiedUser, isMasterclassAlias),
        demoStage: userState,
      })
    } catch (e: any) {
      console.log("event err", e)
    } finally {
      redirectOutFromIframe(navRoutes.IELTS_STUDENT_DASHBOARD)
    }
  }

  return (
    <div>
      <Head>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      {showRedirectCta ? (
        <div className="flex justify-center items-center md:items-stretch h-[100vh] flex-col md:flex-row">
          <Button
            size="large"
            onClick={() => {
              trackGoBackDashboardEvent()
            }}
            customCss="self-center"
          >
            Go To Dashboard
          </Button>
          <img
            src={`${process.env.CDN_URL}/assets/images/lead-capture/background-illustration.svg`}
            className="bottom-0 hidden md:block -z-1 md:absolute"
          />
          <img
            src={`${process.env.CDN_URL}/assets/images/lead-capture/background-illustration-mobile.svg`}
            className="bottom-0 md:hidden w-full -z-1"
          />
        </div>
      ) : (
        <div>
          {checkFormVersionIsTwo(form_version) && demoBookFormJson ? (
            <IeltsEliteProgressiveFormV2
              formJson={demoBookFormJson}
              isLoggedIn={auth?.isValid}
              loggedInNumber={userInfo.phone}
              prefilledData={prefilledData}
              isMasterclassAlias={isMasterclassAlias}
            />
          ) : (
            demoBookFormJson && (
              <IeltsEliteProgressiveForm
                formJson={demoBookFormJson}
                isLoggedIn={auth?.isValid}
                loggedInNumber={userInfo.phone}
                prefilledData={prefilledData}
                formHeading={
                  isDisqualifiedUserFlow ? (
                    <h2 className="font-bold text-[#0A0926] text-center pt-4 text-[20px]">
                      You’re almost there to book your{" "}
                      <LineBreak device="mobile" /> IELTS demo session
                    </h2>
                  ) : null
                }
              />
            )
          )}
        </div>
      )}
    </div>
  )
}
export default withAuth(UserDetailsFormPage, false)
